<template>
    <div v-if="loading" class="flex flex-column justify-content-center align-items-center">
        <h4 class="text-center">Loading...</h4>
        <ScaleLoader class="m-auto" />
    </div>
    <div v-if="!loading" class="">
        <h5 class="mb-4">Manage licence</h5>
        <div class="grid card">
            <div class="form-field col-6">
                <div class="text-500 font-medium">Company</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">{{ license.company?.name }}</div>
            </div>
            <div class="form-field col-6">
                <div class="text-500 font-medium">User</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">
                    {{ license.user?.name }} {{ license.user?.surname }}
                </div>
            </div>
            <div class="form-field col-6">
                <div class="text-500 font-medium">Product</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">
                    {{ license.product?.code }} - {{ license.product?.description }}
                </div>
            </div>
            <div class="form-field col-6">
                <div class="text-500 font-medium">Standard duration (number of months)</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">
                    {{ license.auto_renew }}
                </div>
            </div>
            <div class="form-field col-6">
                <div class="text-500 font-medium">Start date</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">
                    {{ license.start_date }}
                </div>
            </div>
            <div class="form-field col-6">
                <div class="text-500 font-medium">End date</div>
                <div class="text-900 w-full md:flex-order-0 flex-order-1">
                    <b>{{ license.end_date }}</b>
                </div>
            </div>

            <template
                v-if="
                    license.product?.code?.startsWith('CST-')
                "
            >
                <divider />

                <div class="form-field col-6">
                    <div class="text-500 font-medium">Current credits granted on this license</div>
                    <div class="text-900 w-full md:flex-order-0 flex-order-1">
                        <span :class="{ highlight: highlightEffect }">
                            <b class="m-3">{{ license.token }}</b>
                        </span>
                    </div>
                </div>
                <div class="form-field col-6">
                    <div class="flex justify-content-start">
                        <InputText
                            placeholder="Credits"
                            v-model="deltaCredits"
                        />
                        <Button
                            icon="pi pi-shopping-cart"
                            class="p-button-rounded p-button-success ml-2"
                            v-tooltip="'Manage this License'"
                            @click="onAddCredits"
                            label="Change credits by"
                            :loading="deltaCreditsLoading"
                        />
                        <small class="mt-2 ml-3">
                            Use positive numbers to increase the credit, negative to reduce it.
                        </small>
                    </div>
                </div>
            </template>

            <div class="form-field col-9">
                <div class="flex">
                    <template v-if="validityCheck(license.startDateJs, license.endDateJs) === -1">
                        <Badge class=" text-white mr-2 " severity="danger" size="large" v-tooltip="`Expired`">
                            <span> <i class="pi pi-times" /></span> The license is <b>Expired</b>
                        </Badge>
                    </template>
                    <template v-else-if="validityCheck(license.startDateJs, license.endDateJs) === 0">
                        <Badge class=" text-white mr-2" severity="success" size="large" v-tooltip="`Live`">
                            <span> <i class="pi pi-check" /></span> The license is <b>Valid</b>
                        </Badge>
                    </template>
                    <template v-else>
                        <Badge class=" text-white mr-2" severity="warning" size="large" v-tooltip="`Future`">
                            <span> <i class="pi pi-clock" /></span> The license is <b>Future</b>
                        </Badge>
                    </template>
                </div>
            </div>

            <div class="form-field col-3">
                <div class="flex justify-content-end">
                    <Button
                        label="Renew"
                        class="p-button-primary"
                        icon="pi pi-bolt"
                        @click="onRenew"
                    />
                    <Button
                        disabled
                        label="Suspend"
                        class="p-button-help ml-2"
                        icon="pi pi-calendar"
                        @click="onSuspend"
                    />
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        class="p-button-warning ml-2"
                        @click="onCancel"
                    />
                </div>
            </div>
        </div>
    </div>

    <Dialog
        v-model:visible="displayRenewDialog"
        modal
        header="Renew licence" :style="{ width: '50rem' }"
    >
        <RenewLicense
            :id="props.id"
            :currentLicenseEndDate="currentLicenseEndJsDate"
            :standardMonths="license.auto_renew"
            @hideDialog="displayRenewDialog = false"
        />

    </Dialog>
</template>

<script setup>

import { defineProps, onBeforeMount, ref, watch } from 'vue';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';
import { DateTime } from 'luxon';
import { bool } from 'yup';
import { useRouter } from 'vue-router';
import RenewLicense from '@/products/pha/modules/license/view/components/RenewLicense.vue';

const router = useRouter();

const props = defineProps({
    id: {
        type: [String, Number],
        required: false,
    },
});

const license = ref({
    id: null|Number,
    auto_renew: null|bool,
    company: null|{},
    company_id: null|Number,
    product: null|{},
    product_id: null|Number,
    reminder_date: null|String,
    start_date: null|String,
    end_date: null|String,
    user: null|{},
    user_id: null|Number,
    token: null|Number,
    startDateJs: {},
    endDateJs: {},
})

const loading = ref(false);
const displayRenewDialog = ref(false);

const deltaCredits = ref(null)
const deltaCreditsLoading = ref(false)
const highlightEffect = ref(false)

const currentLicenseEndJsDate = ref({})

watch(() => props.id, () => {
    loadLicenseDetails()
});

function loadLicenseDetails() {
    loading.value = true
    GlobalRequest.getRequest(`/v2/licenses/${props.id}`).then((res) => {
        if (res.statusCode === 200) {
            if (Object.hasOwn(res, 'data')) {
                license.value = res.data


                if (license.value.reminder_date?.length !== null && license.value.reminder_date?.length > 0) {
                    let jsDate = DateTime.fromISO(res.data?.reminder_date)

                    if (!jsDate.isValid) {
                        license.value.reminder_date = null
                    } else {
                        license.value.reminder_date = jsDate?.setLocale('en-GB').toLocaleString({
                            weekday: 'short',
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                        })
                    }
                }

                if (license.value.start_date?.length !== null && license.value.start_date?.length > 0) {
                    let jsDate = DateTime.fromISO(res.data?.start_date)

                    if (!jsDate.isValid) {
                        license.value.start_date = null
                    } else {
                        license.value.startDateJs = jsDate.toJSDate()
                        license.value.start_date = jsDate?.setLocale('en-GB').toLocaleString({
                            weekday: 'short',
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                        })
                    }
                }

                if (license.value.end_date?.length !== null && license.value.end_date?.length > 0) {
                    let jsDate = DateTime.fromISO(res.data?.end_date)

                    if (!jsDate.isValid) {
                        license.value.end_date = null
                    } else {
                        license.value.endDateJs = jsDate.toJSDate()
                        currentLicenseEndJsDate.value = jsDate
                        license.value.end_date = jsDate?.setLocale('en-GB').toLocaleString({
                            weekday: 'short',
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                        })
                    }
                }
            }
        }
    }).catch((err) => {
        console.error(err)
        showToast(err, 'Something went wrong');
    }).finally(() => {
        loading.value = false
    })
}

onBeforeMount(() => {
    loadLicenseDetails()
})

function onRenew() {
    displayRenewDialog.value = true
}

function onSuspend() {
    console.log('suspend')
}

function onCancel() {
    router.go(-1)
}

function onAddCredits() {

    if (deltaCredits.value !== null) {
        deltaCreditsLoading.value = true

        GlobalRequest.postRequest(`/v2/licenses/${license.value.id}/token`, {
            token: deltaCredits.value
        }).then((result) => {
            if (result.statusCode === 200) {
                console.log('result', result)

                license.value.token = result.data.token

                deltaCredits.value = null

                // Trigger the highlight effect
                highlightEffect.value = true;
                setTimeout(() => {
                    highlightEffect.value = false;
                }, 1000); // 1 second effect
            }
        }).finally(() => {
            deltaCreditsLoading.value = false
        })
    }
}

/**
 *
 * -1   Expired
 *  1   Future
 *  0   Running
 *
 * @param start
 * @param end
 * @returns {number}
 */
function validityCheck(start, end) {

    if (typeof start !== 'undefined' && typeof end !== 'undefined') {
        console.log('validityCheck', start, end)
        const jsDateStart = DateTime.fromJSDate(start).startOf("day")
        const jsDateEnd = DateTime.fromJSDate(end).startOf("day")

        const today = DateTime.now().startOf("day")

        if (jsDateEnd < today) {
            return -1
        } else if (jsDateStart > today) {
            return 1
        } else {
            return 0
        }
    } else {
        return 0
    }
}

</script>

<style scoped lang="scss">
.highlight {
    background-color: yellow;
    color: darkred;
    transition: background-color 1s ease;
}

.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}
</style>